import type {FunctionComponent} from 'react';
import {useTranslation} from '@app/hooks/use-translation';
import {useRouter} from 'next/router';
import {Box, Heading, Text, Flex} from '@tempo/core';
import {LandingButtonLink} from '@app/components/landing-btn-link';
import {Image} from '@app/components/Image';
import {config} from '@modules/config';
import type {LandingProps} from '@app/types/next';

type AccessibilityPlatform = 'web' | 'ios' | 'android';

interface AccessibilityProps extends LandingProps {
	platform: AccessibilityPlatform;
}

const AccessibilityPage: FunctionComponent<AccessibilityProps> = ({
	platform,
}) => {
	const {t} = useTranslation({ns: ['accessibility']});
	const {locale} = useRouter();
	const linkLanguage = locale === 'fr' ? 'fr' : 'en';

	return (
		<Box maxW={'container.sm'} mx="auto" textAlign="center">
			<Heading as="h1" fontSize="display.s" pb="spacing.xl">
				{t('footer_title_accessibility_web')}
			</Heading>
			<Text mt="spacing.m" pb="spacing.3xl">
				{t('accessibilityact_text_maindescription_web')}
			</Text>
			<Flex
				pt="spacing.3xl"
				pb="spacing.xl"
				direction={{base: 'column-reverse', md: 'row'}}
				align="start"
				gap="spacing.xl"
			>
				<Box textAlign="left">
					<Heading as="h2" fontSize="heading.xl">
						{t('accessibilityact_title_statement_web')}
					</Heading>
					<Text mt="spacing.m">
						{t('accessibilityact_text_statementdescription_web')}
					</Text>
					<LandingButtonLink
						mt="spacing.m"
						href={`${config.get('deezerStaticUrl')}/pdfs/legal/accessibility_statement_${platform}_${linkLanguage}_v1025.pdf`}
					>
						{t('accessibilityact_action_readstatement_web')}
					</LandingButtonLink>
				</Box>
				<Image
					src="3dacea1144c63cd4462c9d9d1688bd3d.jpg"
					alt={t('accessibilityact_title_statement_web')}
					w={{base: '200px', md: '200px'}}
					objectFit="cover"
					aspectRatio="200/200"
					minW="200px"
				/>
			</Flex>
			<Flex
				pt="spacing.xl"
				direction={{base: 'column', md: 'row'}}
				align="start"
				gap="spacing.xl"
			>
				<Image
					src="8ddb84f023bdb823409f8aba7f1e9234.jpg"
					alt={t('accessibilityact_title_multiyearplan_web')}
					w={{base: '200px', md: '200px'}}
					objectFit="cover"
					aspectRatio="200/200"
					minW="210px"
				/>
				<Box textAlign="left">
					<Heading as="h2" fontSize="heading.xl">
						{t('accessibilityact_title_multiyearplan_web')}
					</Heading>
					<Text mt="spacing.m">
						{t('accessibilityact_title_multiyearplandescription_web')}
					</Text>
					<LandingButtonLink
						mt="spacing.m"
						mb="spacing.xl"
						href={`${config.get('deezerStaticUrl')}/pdfs/legal/digital_accessibility_actionplan_${linkLanguage}.pdf`}
					>
						{t('accessibilityact_action_readmultiyearplan_web')}
					</LandingButtonLink>
				</Box>
			</Flex>
		</Box>
	);
};

export default AccessibilityPage;
